import { CopyIcon, GemIcon2, SaveIcon } from "@/components/icons";
import { RefreshIcon } from "@/components/icons";
import { env } from "@/env";
import { useLanguage } from "@/hooks/useLanguage";
import { authStore } from "@/lib/auth/store.tsx";
import { cn, copyText } from "@/lib/utils";
import { m } from "@/paraglide/messages";
import { localizeHref } from "@/paraglide/runtime";
import { useStore } from "@tanstack/react-store";
import html2canvas from "html2canvas-pro";
import { QRCodeCanvas } from "qrcode.react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

// `${env.VITE_CDN_HOST}/share/
const cdnHost = env.VITE_CDN_HOST;

const inviteImgs = [
	`${cdnHost}/share/1.webp`,
	`${cdnHost}/share/2.webp`,
	`${cdnHost}/share/3.webp`,
	`${cdnHost}/share/4.webp`,
	`${cdnHost}/share/5.webp`,
	`${cdnHost}/share/6.webp`,
	`${cdnHost}/share/7.webp`,
	`${cdnHost}/share/8.webp`,
];

export default function InviteCard({
	cover_image,
	invite_url,
	disabledRefresh,
}: { cover_image?: string; invite_url?: string; disabledRefresh?: boolean }) {
	const cardRef = useRef<HTMLDivElement>(null);

	// 随机选一张图片
	const [randomImg, setRandomImg] = useState(() => {
		const idx = Math.floor(Math.random() * inviteImgs.length);
		return inviteImgs[idx];
	});

	// 预加载下一张图片
	useEffect(() => {
		const currentIndex = inviteImgs.indexOf(randomImg);
		const nextIndex = (currentIndex + 1) % inviteImgs.length;
		const nextImgSrc = inviteImgs[nextIndex];

		const img = new window.Image();
		img.src = nextImgSrc;
	}, [randomImg]);

	const user = useStore(authStore, (s: typeof authStore.state) => s.user);
	const inviteUrl =
		invite_url ||
		localizeHref(`${location.origin}?inviteCode=${user?.invite_code ?? ""}`);

	const handleSave = async () => {
		if (cardRef.current) {
			try {
				const canvas = await html2canvas(cardRef.current, {
					useCORS: true,
					scale: 4,
					backgroundColor: "transparent",
					ignoreElements: (el) => {
						return (
							el.classList?.contains("refresh-btn") ||
							el.classList?.contains("backface-block")
						);
					},
				});
				const link = document.createElement("a");
				link.href = canvas.toDataURL("image/png");
				link.download = "invite-card.png";
				link.click();
			} catch (error) {
				toast.error(m.save_image_error());
			}
		}
	};

	const handleCopy = async () => {
		await copyText(inviteUrl);
		toast.success(m.copy_success());
	};

	const handleRandomImg = () => {
		const idx = Math.floor(Math.random() * inviteImgs.length);
		setRandomImg(inviteImgs[idx]);
	};

	const finalCover = cover_image || randomImg;

	const lang = useLanguage();

	return (
		<div className="flex flex-col items-center w-full">
			<div className="text-sm mb-6 max-w-[617px] text-left font-medium text-t2">
				{m.invite_text_start()}
				{lang === "zh" ? (
					<>
						<GemIcon2 className="inline mx-1 text-yellow-400" />
						{m.invite_text_middle()}
						<span className="text-main1">
							{" "}
							{m.invite_text_num({ num: "10%" })}
						</span>
						{m.invite_text_end()}
					</>
				) : (
					<>
						<span className="text-main1 mx-1">
							{m.invite_text_num({ num: "10%" })}
						</span>
						{m.invite_text_middle()}

						<GemIcon2 className="inline mx-1 text-yellow-400" />
					</>
				)}
				{m.invite_text_end()}
			</div>
			<div className="flex justify-center w-full">
				<div
					ref={cardRef}
					className="relative rounded-[20px] px-[18px] py-[10px] w-[313px] h-[403px] flex flex-col mx-auto overflow-hidden"
				>
					<div
						className={cn(
							"absolute z-3 top-4 right-4 cursor-pointer",
							disabledRefresh && "hidden",
						)}
						onClick={() => handleRandomImg()}
					>
						<RefreshIcon className="w-6 h-6 [&>path]:fill-white hover:[&>path]:fill-main1 refresh-btn" />
					</div>
					<img
						src="/logo-light.svg"
						alt="logo"
						className="w-[125px] h-auto relative z-3"
					/>
					<div className="backface-block w-full h-[92px] absolute z-2 will-change-transform backface-hidden -top-1 left-0 bg-gradient-to-b from-[rgba(0,0,0,0.50)] to-transparent" />
					<img
						src={finalCover}
						alt={m.invite_image()}
						className="absolute top-0 z-0 left-0 w-[313px] h-[403px] object-cover rounded-[20px] mb-4"
						onError={(e) => console.log(e)}
					/>
					<div className="mt-2 absolute bottom-[15px] left-1/2 ml-[-36.5px] w-[73px] h-[73px] bg-white flex justify-center items-center rounded-xl">
						<QRCodeCanvas value={inviteUrl} size={65} className="rounded-lg" />
					</div>
				</div>
			</div>
			<div className="flex items-center gap-[5px] mt-6 mb-14">
				<button
					type="button"
					className="w-13 h-10 flex items-center justify-center rounded-3xl bg-c1 cursor-pointer transition hover:bg-main1 hover:[&>svg>path]:stroke-white dark:[&>svg>path]:stroke-white dark:hover:bg-main1"
					onClick={handleSave}
				>
					<SaveIcon />
				</button>
				<div className="flex items-center w-[260px] h-10 relative bg-bg2 rounded-[20px] py-2 px-5 pr-0">
					<input
						className="w-full h-full flex-1 text-sm focus:outline-none bg-transparent"
						value={inviteUrl}
						readOnly
					/>
					<button
						type="button"
						className="w-[71px] cursor-pointer flex items-center justify-center gap-1 h-10 bg-main1 text-w1 dark:[&>svg>g>path]:stroke-w1 rounded-3xl hover:brightness-90 transition"
						onClick={handleCopy}
					>
						<CopyIcon />
						{m.copy()}
					</button>
				</div>
			</div>
		</div>
	);
}
